<script setup>
import { computed } from 'vue'
import { visualMode, hasLottie } from '~/utils/block-visual.js'

// Components
import { defineAsyncComponent } from 'vue'
const GlobalButton = defineAsyncComponent(() =>
  import('~/components/globals/Button.vue')
)
const LogoGardenCarousel = defineAsyncComponent(() =>
  import('~/components/blocks/logoGarden/carousel.vue')
)

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

const showLottie = computed(() => visualMode(props.block) === 'lottie' && hasLottie(props.block))
const showKv = computed(() => visualMode(props.block) === 'kv' && !!props.block?.conversationBox?.length)

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="hero-section-v3 section-mb">
  <div class="container flex flex-col items-center">

    <div v-html="block?.heading?.html"
        class="hero-section-v3__heading"></div>

    <div v-if="block?.button?.url || block?.button2?.url"
    class="hero-section-v3__buttons">
      <!-- Button 1 -->
      <GlobalButton
        v-if="block?.button?.title"
        :url="block.button.url"
        :title="block.button.title"
        :buttonSettings="block?.buttonSettings"
        class="hero-section-v3__btn"
      />

      <!-- Button 2 -->
      <GlobalButton
        v-if="block?.button2?.title"
        :url="block.button2.url"
        :title="block.button2.title"
        :type="block?.buttonSettings2?.linkType"
        :size="block?.buttonSettings2?.linkSize"
        :icon="block?.buttonSettings2?.linkIcon"
        :popup-enabled="block?.buttonSettings2?.popupEnabled"
        class="hero-section-v3__btn"
      />
      <!-- End buttons -->
    </div>

    <div v-if="showKv || showLottie || block?.imagesDesktop?.length || block?.imagesMobile?.length"
      class="hero-section-v3__image"
      :class="{ '--narrow-width': block?.heroWidth }">
      <GlobalsConversationImage
        v-if="showKv"
        :entry="block"
        desktop-field="imagesDesktop"
        mobile-field="imagesMobile"
      />
      <GlobalsLottieVisual
        v-else-if="showLottie"
        :entry="block"
        desktop-field="imagesDesktop"
        mobile-field="imagesMobile"
        :width="block?.heroWidth ? 1160 : 1552"
        :height="660"
      />
      <GlobalsImage
        v-else
        :landscape="block?.imagesDesktop"
        :portrait="block?.imagesMobile"
        :quality="85"
        loading="eager"
        :max-width="block?.heroWidth ? 1160 : 1552"
        :max-width-mobile="398"
        class="hero-section-v3__poster"
        :alt="block?.heading?.plainText"
      />
    </div>

  </div>

  <div class="hero-section-v3__logo-garden container">
    <LogoGardenCarousel
      :logoGardenImages="block?.['logoGardenContent']?.[0]?.images"
      :class="{ '--narrow-width': block?.heroWidth, '--narrow-logo-garden': block?.logoGardenWidth }"
    />
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.hero-section-v3 {
  padding-top: 36px;
}

.hero-section-v3__heading {
  text-align: center;
}

.hero-section-v3__heading :deep(p) {
  font-size: 16px;
  line-height: 136%;
  letter-spacing: -0.32px;
  color: var(--color-gray-500);
  margin-top: 16px;

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 136%;
    letter-spacing: -0.4px;
    max-width: 700px;
    margin-inline: auto;
    margin-top: 24px;
  }
}

@media (max-width: 1023px) {
  .hero-section-v3__heading :deep(br) {
    display: none;
  }
}

.hero-section-v3__buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;

  @media (min-width: 1024px) {
    margin-top: 32px;
  }
}

.hero-section-v3__image {
  margin-top: 44px;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.hero-section-v3__logo-garden {
  margin-top: 24px;
}

.hero-section-v3__logo-garden :deep(.swiper img) {
  height: 30px !important;
}

@media (max-width: 1023px) {
  .hero-section-v3__logo-garden {
    padding-left: 0;
    padding-right: 0;
  }
}

@media (min-width: 1024px) {

  .hero-section-v3 {
    padding-top: 80px;
    margin-bottom: 140px;
  }

  .hero-section-v3__image {
    margin-top: 64px;
  }

  .hero-section-v3__logo-garden {
    margin-top: 32px;
  }

  .hero-section-v3__logo-garden :deep(.swiper img) {
    height: 52px !important;
  }

}

.--narrow-width {
  max-width: 1160px;
  margin-inline: auto;
}

.--narrow-logo-garden {
  max-width: 1205px;
  margin-inline: auto;
}

@media (min-width: 1440px) {
  .hero-section-v3:has(.--narrow-width) {
    padding-top: 94px;
    margin-bottom: 188px;
  }
}

.hero-section-v3__heading :deep(.--lemony-text) {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    left: -3px;
    right: -3px;
    height: 16px;
    background-color: #F2FF66;
    z-index: -1;

    @media (min-width: 1024px) {
      bottom: 10px;
      left: -5px;
      right: -3px;
      height: 16px;
    }
  }
}

</style>
